/**
 * The Business Intelligence section's menu (CU-869fmhmx8 D13, Curro's decision 6; CU-869fmjzrh): dashboards first, then Europcar
 * (until the menu task moves it to Home), Pricing Environment on its own, Leading Analyzer and "Advanced (BI)" last.
 * The section header opens Advanced (BI) for a user with `bi`; without it, the first entry.
 *
 * Pure and alias-free, so `node --test` runs it: the routes come from `BUSINESS_LOGISTICS_ROUTES` at the call site.
 */

export const MENU_KEYS = Object.freeze({
	SEASON_VS_LY: 'seasonVsLastYear',
	PRICE_CURVE: 'priceCurve',
	// CU-869fmjzrh: this exact value, the Advanced BI goals route by it
	OFFICE_NETWORK: 'officeNetwork',
	EUROPCAR: 'europcar',
	PRICING_ENV: 'pricingEnvironment',
	LEADING: 'leadingAnalyzer',
	ADVANCED: 'advancedBi',
})

// The entries that wear "NEW" and until when (`menuBadge`). `null` = not dated yet: set the date (about a month after the
// release) when the entry is cherry-picked to `master`, never on `testing` (CU-869fmjzrg, TL N2)
export const NEW_UNTIL = Object.freeze({ priceCurve: null, officeNetwork: null })

/**
 * The section's entries the user may see, in menu order: `[{ key, path, title, badge? }]`. Order (Curro's decision 8,
 * CU-869fmjzrg): Season vs last year · Price curve of a date · My office network · … · Advanced (BI) last. `today`
 * decides the "NEW" badges; without it none is shown.
 */
export function businessLogisticsChildren(features = {}, routes, { today = null, newUntil = NEW_UNTIL } = {}) {
	const f = features || {}
	const badge = key => {
		const b = menuBadge(key, today, newUntil)
		return b ? { badge: b } : {}
	}
	return [
		...(f.bi ? [{ key: MENU_KEYS.SEASON_VS_LY, path: routes.SEASON_VS_LY_PATH, title: routes.SEASON_VS_LY_TITLE }] : []),
		...(f.bi ? [{ key: MENU_KEYS.PRICE_CURVE, path: routes.PRICE_CURVE_PATH, title: routes.PRICE_CURVE_TITLE, ...badge(MENU_KEYS.PRICE_CURVE) }] : []),
		// decision 8 of CU-869fmjzrg: Season · Price curve of a date · My office network · … · Advanced (BI) last
		...(f.bi ? [{ key: MENU_KEYS.OFFICE_NETWORK, path: routes.OFFICE_NETWORK_PATH, title: routes.OFFICE_NETWORK_TITLE, ...badge(MENU_KEYS.OFFICE_NETWORK) }] : []),
		...(f.europcar ? [
			{ key: MENU_KEYS.EUROPCAR, path: routes.EUROPCAR_PATH, title: routes.EUROPCAR_MENU_TITLE },
			{ key: MENU_KEYS.PRICING_ENV, path: routes.PRICING_ENV_PATH, title: routes.PRICING_ENV_TITLE },
		] : []),
		...(f.leading_price ? [{ key: MENU_KEYS.LEADING, path: routes.LEADING_ANALYZER_PATH, title: routes.LEADING_ANALYZER_TITLE }] : []),
		...(f.bi ? [{ key: MENU_KEYS.ADVANCED, path: routes.BI_PATH, title: routes.BI_TITLE }] : []),
	]
}

/** Where the section header lands: Advanced (BI) with `bi`, else the first entry; `null` when the section is not there. */
export function sectionIndexPath(features = {}, routes) {
	if (features?.bi) return routes.BI_PATH
	return businessLogisticsChildren(features, routes)[0]?.path || null
}

/**
 * The "NEW" mark of a menu entry (CU-869fmjzrg, Curro's decision 13; TL N2). `newUntil` = `{ key: 'YYYY-MM-DD' | null }`:
 * an entry listed there wears it until that day included, and then it goes on its own. `null` = not dated yet: shows
 * (on `testing`). The date is set when the entry is cherry-picked to `master`, so it never expires before PROD sees it.
 * Pure: `today` comes from the caller.
 */
export function menuBadge(key, today, newUntil = {}) {
	if (!Object.prototype.hasOwnProperty.call(newUntil || {}, key) || !(today instanceof Date)) return null
	const until = newUntil[key]
	if (until === null) return 'NEW'
	const pad = n => String(n).padStart(2, '0')
	const iso = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
	return iso <= until ? 'NEW' : null
}
